import { FunctionComponent, FC, useEffect } from 'react';
import {
  Box,
  Step,
  StepLabel,
  Stepper,
  Typography,
  styled,
} from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import Page from '../components/Page';
import {
  FinalizePurchaseIcon,
  SelectAnOfferIcon,
  TellAboutYourselfIcon,
} from '../icons';
import { addToContext } from '../services/contextManager';
import { AppView, START_PAGE_STEPS, START_PAGE_TITLES } from '../constants';
import { setFirstLoadAfterLogin } from '../services/pinManager';
import useFeedbackSnackbar from '../hooks/useFeedbackSnackbar';
import {
  FEEDBACK_TYPE,
  clearFeedbackSession,
  getFeedbackSession,
} from '../services/feedbackManager';
import { useGetMerchantConfigurationOptions } from '../services/configManager';
import SelfApplyStartActions from '../components/SelfApplyStartActions';
import { StoreViewText } from '../components/StoreView';
import { VerticalSpace } from '@chargeafter/ui';
import UserMenu from '../components/UserMenu';
import { getStoreFromStorage } from '../services/storeManager';
import { MainFlowStartActions } from '../components/MainFlowStartActions';
import { MainPaperLayout } from '../components/MainPaperLayout';
import { SecondaryPaperLayout } from '../components/SecondaryPaperLayout';

const startFeedbackBar: FEEDBACK_TYPE[] = [FEEDBACK_TYPE.LOGGED_OUT];

const StyledUserMenuBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'end',
  alignItems: 'center',
  gap: theme.spacing(4),
}));

const Start: FunctionComponent = () => {
  const { user } = useAuth0();
  const [feedback] = useFeedbackSnackbar();
  const { consumerSelfApply } = useGetMerchantConfigurationOptions();
  const storeName = getStoreFromStorage()?.name;
  const feedbackType = getFeedbackSession() as FEEDBACK_TYPE;

  useEffect(() => {
    if (feedbackType && startFeedbackBar.includes(feedbackType)) {
      feedback({ type: feedbackType });
    }
    clearFeedbackSession();
  }, [feedback, feedbackType]);

  setFirstLoadAfterLogin(!user);
  addToContext('userEmail', user?.email);

  return (
    <Page
      pageName={
        consumerSelfApply ? AppView.StartSelfApplyFlow : AppView.StartMainFlow
      }
      disableGutters
      showBar={!consumerSelfApply}
      disableStartOver
    >
      {!consumerSelfApply ? (
        <MainPaperLayout
          header={START_PAGE_TITLES.mainFlow}
          content={<StartPageContent />}
          actionArea={<MainFlowStartActions />}
        />
      ) : (
        <>
          <StyledUserMenuBox>
            <StoreViewText storeName={storeName ?? 'No store selected'} />
            <UserMenu />
          </StyledUserMenuBox>
          <VerticalSpace units={2} />
          <SecondaryPaperLayout
            header={START_PAGE_TITLES.selfApplyFlow}
            content={<StartPageContent />}
            actionArea={<SelfApplyStartActions />}
          />
        </>
      )}
    </Page>
  );
};

export default Start;

const IconWrapper = styled(Box)(({ theme }) => ({
  height: theme.spacing(18),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  svg: {
    height: theme.spacing(11),
    width: theme.spacing(11),
    color: theme.palette.primary.main,
  },
}));

const StepContentTypography = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(1, 4),
  textAlign: 'center',
  fontSize: '12pt',
}));

type StartPageContentProps = {};
const StartPageContent: FC<StartPageContentProps> = () => {
  const consumerSelfApply =
    useGetMerchantConfigurationOptions().consumerSelfApply;

  const steps = [
    {
      icon: (
        <IconWrapper>
          <TellAboutYourselfIcon />
        </IconWrapper>
      ),
      label: START_PAGE_STEPS.tellAboutYourself.label,
      content: START_PAGE_STEPS.tellAboutYourself.content,
    },
    {
      icon: (
        <IconWrapper>
          <SelectAnOfferIcon />
        </IconWrapper>
      ),
      label: consumerSelfApply
        ? START_PAGE_STEPS.selectOffer.selfApplyFlow.label
        : START_PAGE_STEPS.selectOffer.mainFlow.label,
      content: consumerSelfApply
        ? START_PAGE_STEPS.selectOffer.selfApplyFlow.content
        : START_PAGE_STEPS.selectOffer.mainFlow.content,
    },
    {
      icon: (
        <IconWrapper>
          <FinalizePurchaseIcon />
        </IconWrapper>
      ),
      label: consumerSelfApply
        ? START_PAGE_STEPS.finializePurchase.selfApplyFlow.label
        : START_PAGE_STEPS.finializePurchase.mainFlow.label,
      content: consumerSelfApply
        ? START_PAGE_STEPS.finializePurchase.selfApplyFlow.content
        : START_PAGE_STEPS.finializePurchase.mainFlow.content,
    },
  ];

  return (
    <Box>
      <Stepper alternativeLabel>
        {steps.map((step, idx) => (
          <Step active key={idx}>
            {step.icon}
            <StepLabel>
              <span style={{ fontWeight: 700 }}>{step.label}</span>
            </StepLabel>
            <StepContentTypography variant="body1">
              {step.content}
            </StepContentTypography>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
};
